import ky from 'ky';
import { UserRoles } from '../constants';
import { oktaAuth } from '../App';
import { AccessToken } from '@okta/okta-auth-js';

let cachedToken: AccessToken | null = null;
let cachedUserRoles: string[] = [];
let tokenExpiry: number = 0;

const extendKY = (authz: string, authn: string) =>
  ky.extend({
    hooks: {
      beforeRequest: [
        (request) => {
          request.headers.set('Authorization', authz);
          request.headers.set('AuthN', window.btoa(authn));
        },
      ],
    },
    throwHttpErrors: true,
  });

async function getApiInstance() {
  const now = Date.now();

  // Use cached token if valid and not expired within 5 minute buffer
  if (cachedToken && cachedToken.accessToken && now < tokenExpiry - 300000) {
    return extendKY(cachedToken.accessToken, cachedUserRoles.join(','));
  }

  const token = (await oktaAuth.tokenManager.get('accessToken')) as AccessToken;
  if (!token || !token.accessToken) {
    cachedToken = null;
    cachedUserRoles = [];
    tokenExpiry = 0;
    throw new Error('Failed to get an access token');
  }

  const user = await oktaAuth.getUser();
  cachedUserRoles = [];
  if (user?.groups && Array.isArray(user.groups)) {
    for (let [role, group] of Object.entries(UserRoles)) {
      if (user.groups.includes(group)) {
        cachedUserRoles.push(role.toLowerCase());
      }
    }
  }

  cachedToken = token;
  tokenExpiry = token.expiresAt ? token.expiresAt * 1000 : now + 3600000;

  return extendKY(token.accessToken, cachedUserRoles.join(','));
}

const authenticatedApi = {
  get: async <T>(url: string, options?: any, parseJSON = true): Promise<T | Response> => {
    try {
      const apiWithToken = await getApiInstance();
      const request = apiWithToken.get(url, options);
      if (parseJSON) {
        return request.json<T>();
      }
      return request;
    } catch (error) {
      console.error(`[API] Error at GET ${url}`, error);
      throw error;
    }
  },
  post: async <T>(url: string, options?: any, isBodyOnly = true): Promise<T> => {
    try {
      const apiWithToken = await getApiInstance();
      if (isBodyOnly) return await apiWithToken.post(url, options).json<T>();
      else return (await apiWithToken.post(url, options)) as T;
    } catch (error) {
      console.error(`[API] Error at POST ${url}`, error);
      throw error;
    }
  },
  put: async <T>(url: string, options?: any): Promise<T> => {
    try {
      const apiWithToken = await getApiInstance();
      return await apiWithToken.put(url, options).json<T>();
    } catch (error) {
      console.error(`[API] Error at PUT ${url}`, error);
      throw error;
    }
  },

  delete: async <T>(url: string, options?: any): Promise<T> => {
    try {
      const apiWithToken = await getApiInstance();
      return await apiWithToken.delete(url, options).json<T>();
    } catch (error) {
      console.error(`[API] Error at DELETE ${url}`, error);
      throw error;
    }
  },
};

export default authenticatedApi;
